import { ReparoApi, BASE_API_URL } from '@api/index';

export interface CreateThreadResponse {
    threadId: string;
}

/**
 * Creates a new reporting agent thread. Returns the threadId to use
 * for subsequent message-stream requests.
 */
export const createReportingThread =
    async (): Promise<CreateThreadResponse> => {
        return ReparoApi<CreateThreadResponse>({
            method: 'POST',
            url: '/reporting/threads',
            body: JSON.stringify({}),
        });
    };

export interface ArtifactShareLinkResponse {
    url: string;
    expiresAt: string;
}

/**
 * Mints a presigned, time-limited download URL for an artifact. The
 * returned URL bypasses our API auth so it's safe to paste into an
 * email body — recipients can fetch without a Bearer token. Backend
 * scopes the link to the caller's merchantId, so users can only sign
 * their own merchant's artifacts.
 *
 * `relativeUrl` is the same /reporting/artifacts/<runId>/<filename>
 * path the SSE event surfaces; this method just extracts the runId
 * and filename and posts them to the share-link endpoint.
 */
export async function createArtifactShareLink(
    relativeUrl: string,
): Promise<ArtifactShareLinkResponse> {
    const match = /\/reporting\/artifacts\/([^/]+)\/([^/?#]+)/.exec(relativeUrl);
    if (!match) {
        throw new Error(`Could not parse runId/filename from ${relativeUrl}`);
    }
    const [, runId, filename] = match;
    return ReparoApi<ArtifactShareLinkResponse>({
        method: 'POST',
        url: `/reporting/artifacts/${runId}/${filename}/share-link`,
    });
}

/**
 * Downloads an artifact from the reporting agent's authenticated
 * artifact endpoint and triggers a browser save dialog. The URL is
 * the relative path returned by execute-report (e.g.
 * "/reporting/artifacts/<runId>/<filename>"); we resolve it against
 * the API base and attach the bearer token.
 */
export async function downloadArtifact(
    relativeUrl: string,
    filename: string,
): Promise<void> {
    const token = localStorage.getItem('token');
    const apiPrefix = BASE_API_URL ? `${BASE_API_URL}/api/v1` : '/api/v1';
    const fullUrl = `${apiPrefix}${relativeUrl}`;

    const response = await fetch(fullUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        throw new Error(`Download failed: HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.append(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
}
